/**
 * Variáveis substituídas na hora da impressão (ver `gerarZplImpressaoBo`
 * em src/contexts/EpcContext). Usadas pelo editor de etiquetas para inserir
 * campos dinâmicos em textos, códigos de barras e QR.
 */
export type VariavelZpl = { id: string; nome: string };

export type GrupoVariaveis = { titulo: string; variaveis: VariavelZpl[] };

export const GRUPOS_VARIAVEIS: GrupoVariaveis[] = [
  {
    titulo: "Item",
    variaveis: [
      { id: "{desc}", nome: "Descrição" },
      { id: "{codigo}", nome: "Código" },
      { id: "{codigosze}", nome: "Código sem zeros à esquerda" },
      { id: "{ean}", nome: "EAN" },
      { id: "{eansemzero}", nome: "EAN sem zeros à esquerda" },
      { id: "{unidade_medida}", nome: "Unidade de medida" },
      { id: "{valor_unidade}", nome: "Valor unitário" },
      { id: "{id_item}", nome: "ID do item" },
      { id: "{id_conta}", nome: "ID da conta" },
    ],
  },
  {
    titulo: "EPC / RFID",
    variaveis: [
      { id: "{epc}", nome: "EPC" },
      { id: "{epcsze}", nome: "EPC sem zeros à esquerda" },
      { id: "{epc6final}", nome: "6 últimos do EPC" },
      { id: "{epc3final}", nome: "3 últimos do EPC" },
      { id: "{sscc}", nome: "SSCC (00)" },
    ],
  },
  {
    titulo: "Serial e contagem",
    variaveis: [
      { id: "{serial}", nome: "Serial" },
      { id: "{serial+1}", nome: "Serial + 1" },
      { id: "{serial+1cze}", nome: "Serial + 1 com zeros" },
      { id: "{serialc2ze}", nome: "Serial 2 dígitos" },
      { id: "{serialc3ze}", nome: "Serial 3 dígitos" },
      { id: "{serialcze}", nome: "Serial 4 dígitos" },
      { id: "{serialc5ze}", nome: "Serial 5 dígitos" },
      { id: "{serialc6ze}", nome: "Serial 6 dígitos" },
      { id: "{serialc7ze}", nome: "Serial 7 dígitos" },
      { id: "{serialc8ze}", nome: "Serial 8 dígitos" },
      { id: "{n_impressao}", nome: "Nº de impressão" },
    ],
  },
  {
    titulo: "Localização",
    variaveis: [
      { id: "{nivel_loc_1}", nome: "Localização nível 1" },
      { id: "{nivel_loc_2}", nome: "Localização nível 2" },
      { id: "{nivel_loc_3}", nome: "Localização nível 3" },
      { id: "{nivel_loc_4}", nome: "Localização nível 4" },
    ],
  },
  {
    titulo: "Data e hora",
    variaveis: [
      { id: "{data}", nome: "Data" },
      { id: "{data2}", nome: "Data (formato 2)" },
      { id: "{data-hora}", nome: "Data e hora" },
      { id: "{dd}", nome: "Dia" },
      { id: "{mm}", nome: "Mês" },
      { id: "{aa}", nome: "Ano (AA)" },
      { id: "{aaaa}", nome: "Ano (AAAA)" },
      { id: "{hh}", nome: "Hora" },
      { id: "{mn}", nome: "Minuto" },
      { id: "{ss}", nome: "Segundo" },
      { id: "{hh_mn_ss_hex}", nome: "HH:MM:SS (hex)" },
      { id: "{dd_mm_hex}", nome: "DD/MM (hex)" },
    ],
  },
  {
    titulo: "Informações complementares",
    variaveis: [
      { id: "{info1}", nome: "Info 1" },
      { id: "{info2}", nome: "Info 2" },
      { id: "{info2sze}", nome: "Info 2 sem zeros" },
      { id: "{info3}", nome: "Info 3" },
      { id: "{info4}", nome: "Info 4" },
      { id: "{info5}", nome: "Info 5" },
      { id: "{info6}", nome: "Info 6" },
      { id: "{info7}", nome: "Info 7" },
      { id: "{info8}", nome: "Info 8" },
      { id: "{info9}", nome: "Info 9" },
      { id: "{info10}", nome: "Info 10" },
      { id: "{codigo_panvel}", nome: "Código Panvel" },
    ],
  },
];

export const TODAS_VARIAVEIS: VariavelZpl[] = GRUPOS_VARIAVEIS.flatMap(
  (g) => g.variaveis,
);

const REGEX_VARIAVEL = /\{[a-zA-Z0-9_+-]+\}/g;

export const variaveisUsadas = (texto: string): string[] =>
  Array.from(new Set(texto.match(REGEX_VARIAVEL) ?? []));

/** Substitui variáveis por valores fictícios para a pré-visualização. */
export const preencherVariaveisExemplo = (texto: string): string =>
  texto.replace(REGEX_VARIAVEL, (token) => {
    const exemplos: Record<string, string> = {
      "{desc}": "PRODUTO EXEMPLO",
      "{codigo}": "000123",
      "{codigosze}": "123",
      "{ean}": "7891234567895",
      "{eansemzero}": "7891234567895",
      "{epc}": "E28011700000020F1A2B3C4D",
      "{epcsze}": "E28011700000020F1A2B3C4D",
      "{epc6final}": "3C4D5E",
      "{epc3final}": "4D5",
      "{sscc}": "(00)178912345678901231",
      "{serial}": "1",
      "{n_impressao}": "1",
      "{unidade_medida}": "UN",
      "{valor_unidade}": "19,90",
    };
    if (exemplos[token]) return exemplos[token];
    if (/^\{serial/.test(token)) return "000001";
    if (/^\{info/.test(token)) return "INFO";
    if (/^\{nivel_loc/.test(token)) return "LOCAL";
    if (token === "{aaaa}") return "2026";
    if (token === "{aa}") return "26";
    return "00";
  });
